import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useToast } from '../../contexts/ToastContext.jsx';
import { ApiError } from '../../utils/apiClient.js';
import { parseApiError } from '../../utils/consolidatedErrorHandling.js';
import { apiRequest } from '../../utils/apiClient.js';

/**
 * User details panel for viewing and editing user information
 */
const UserDetailsPanel = ({
  user,
  onUpdate,
  onDeactivate,
  onReactivate,
  onViewBilling,
  onViewActivity,
  onClose
}) => {
  const { getToken } = useAuth();
  const { showToast } = useToast();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    mobile_phone: '',
    subscription_plan: '',
    status: 'active'
  });


  /**
   * Fetch detailed user information
   */
  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      
      const response = await apiRequest(`/api/admin/users/${user.id}/details`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const details = await response.json();
      setUserDetails(details);
      
      // Initialize edit form with current values
      setEditForm({
        first_name: details.first_name || '',
        last_name: details.last_name || '',
        email: details.email || '',
        mobile_phone: details.mobile_phone || '',
        subscription_plan: details.subscription_plan || 'free',
        status: details.status || 'active'
      });

    } catch (error) {
      console.error('Error fetching user details:', error);
      showToast(error.message || 'Failed to load user details', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle form input changes
   */
  const handleInputChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  /**
   * Save user changes
   */
  const handleSave = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      
      const response = await apiRequest(`/api/admin/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const updatedUser = await response.json();
      setUserDetails(updatedUser);
      setEditing(false);
      onUpdate(updatedUser);
      showToast('User updated successfully', 'success');

    } catch (error) {
      console.error('Error updating user:', error);
      showToast(error.message || 'Failed to update user', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle user deactivation
   */
  const handleDeactivate = () => {
    onDeactivate(user);
  };

  /**
   * Handle user reactivation
   */
  const handleReactivate = () => {
    onReactivate(user);
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Get status badge styling
   */
  const getStatusBadge = (status) => {
    const baseClasses = 'px-3 py-1 rounded-full text-sm font-medium';
    
    switch (status?.toLowerCase()) {
      case 'active':
        return `${baseClasses} bg-green-900/30 text-green-400 border border-green-500/30`;
      case 'inactive':
        return `${baseClasses} bg-red-900/30 text-red-400 border border-red-500/30`;
      case 'suspended':
        return `${baseClasses} bg-yellow-900/30 text-yellow-400 border border-yellow-500/30`;
      default:
        return `${baseClasses} bg-gray-900/30 text-gray-400 border border-gray-500/30`;
    }
  };

  // Load user details when component mounts or user changes
  useEffect(() => {
    if (user?.id) {
      fetchUserDetails();
    }
  }, [user?.id]);

  if (loading && !userDetails) {
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-700 rounded"></div>
            <div className="h-4 bg-gray-700 rounded w-3/4"></div>
            <div className="h-4 bg-gray-700 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-gray-800 rounded-lg border border-gray-700">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex justify-between items-center">
          <h3 className="text-lg font-medium text-white dark:text-white">User Details</h3>
          <div className="flex items-center space-x-2">
            {!editing ? (
              <>
                <button
                  onClick={() => setEditing(true)}
                  className="px-3 py-1 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors dark:text-white"
                >
                  Edit
                </button>
                <button
                  onClick={() => onViewBilling(user)}
                  className="px-3 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors dark:text-white"
                >
                  Billing
                </button>
                <button
                  onClick={() => onViewActivity(user)}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors dark:text-white"
                >
                  Activity
                </button>
                {userDetails?.status === 'active' ? (
                  <button
                    onClick={handleDeactivate}
                    className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors dark:text-white"
                  >
                    Deactivate
                  </button>
                ) : (
                  <button
                    onClick={handleReactivate}
                    className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors dark:text-white"
                  >
                    Reactivate
                  </button>
                )}
              </>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="px-3 py-1 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors disabled:opacity-50 dark:text-white"
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setEditing(false);
                    // Reset form to original values
                    setEditForm({
                      first_name: userDetails?.first_name || '',
                      last_name: userDetails?.last_name || '',
                      email: userDetails?.email || '',
                      mobile_phone: userDetails?.mobile_phone || '',
                      subscription_plan: userDetails?.subscription_plan || 'free',
                      status: userDetails?.status || 'active'
                    });
                  }}
                  className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors dark:text-white"
                >
                  Cancel
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white dark:text-white"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h4 className="text-md font-medium text-white mb-4 dark:text-white">Basic Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1 dark:text-gray-600">
                  First Name
                </label>
                {editing ? (
                  <input
                    type="text"
                    value={editForm.first_name}
                    onChange={(e) => handleInputChange('first_name', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-teal-500 dark:text-white"
                  />
                ) : (
                  <p className="text-gray-300 dark:text-gray-600">{userDetails?.first_name || 'Not provided'}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1 dark:text-gray-600">
                  Last Name
                </label>
                {editing ? (
                  <input
                    type="text"
                    value={editForm.last_name}
                    onChange={(e) => handleInputChange('last_name', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-teal-500 dark:text-white"
                  />
                ) : (
                  <p className="text-gray-300 dark:text-gray-600">{userDetails?.last_name || 'Not provided'}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1 dark:text-gray-600">
                  Email
                </label>
                {editing ? (
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-teal-500 dark:text-white"
                  />
                ) : (
                  <p className="text-gray-300 dark:text-gray-600">{userDetails?.email}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1 dark:text-gray-600">
                  Phone Number
                </label>
                {editing ? (
                  <input
                    type="tel"
                    value={editForm.mobile_phone}
                    onChange={(e) => handleInputChange('mobile_phone', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-teal-500 dark:text-white"
                  />
                ) : (
                  <p className="text-gray-300 dark:text-gray-600">{userDetails?.mobile_phone || 'Not provided'}</p>
                )}
              </div>
            </div>
          </div>

          {/* Account Status */}
          <div>
            <h4 className="text-md font-medium text-white mb-4 dark:text-white">Account Status</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1 dark:text-gray-600">
                  Status
                </label>
                {editing ? (
                  <select
                    value={editForm.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-teal-500 dark:text-white"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                ) : (
                  <span className={getStatusBadge(userDetails?.status)}>
                    {userDetails?.status || 'Active'}
                  </span>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1 dark:text-gray-600">
                  Subscription Plan
                </label>
                {editing ? (
                  <select
                    value={editForm.subscription_plan}
                    onChange={(e) => handleInputChange('subscription_plan', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-teal-500 dark:text-white"
                  >
                    <option value="free">Free</option>
                    <option value="basic">Basic</option>
                    <option value="pro">Pro</option>
                    <option value="premium">Premium</option>
                  </select>
                ) : (
                  <p className="text-gray-300 capitalize dark:text-gray-600">{userDetails?.subscription_plan || 'Free'}</p>
                )}
              </div>
            </div>
          </div>

          {/* Account Activity */}
          <div>
            <h4 className="text-md font-medium text-white mb-4 dark:text-white">Account Activity</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1 dark:text-gray-600">
                  Created At
                </label>
                <p className="text-gray-300 dark:text-gray-600">{formatDate(userDetails?.created_at)}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1 dark:text-gray-600">
                  Last Login
                </label>
                <p className="text-gray-300 dark:text-gray-600">{formatDate(userDetails?.last_login)}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1 dark:text-gray-600">
                  Total Parrots
                </label>
                <p className="text-gray-300 dark:text-gray-600">{userDetails?.parrot_count || 0}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1 dark:text-gray-600">
                  Total Calls
                </label>
                <p className="text-gray-300 dark:text-gray-600">{userDetails?.call_count || 0}</p>
              </div>
            </div>
          </div>
        </div>
      </div>


    </>
  );
};

export default UserDetailsPanel;
